import React, { useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Divider, Alert, CircularProgress, Checkbox, FormControlLabel,
  IconButton, InputAdornment
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import { useAuth } from '../../context/AuthContext';

// Google SSO is disabled by default: ELM's dveracity-elm Identity Platform pool
// only has Email/Password enabled, so the Google button throws
// auth/operation-not-allowed. To re-enable, turn on the Google provider in
// Identity Platform AND build with REACT_APP_ENABLE_GOOGLE_SSO=true.
const GOOGLE_SSO_ENABLED = process.env.REACT_APP_ENABLE_GOOGLE_SSO === 'true';

export default function LoginPage() {
  const { login, resetPassword, isAuthenticated, loading, authMode, error: authError } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';

  // Bypass mode: never show login
  if (authMode === 'bypass' || isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleGoogle = async () => {
    setError('');
    setSubmitting(true);
    try {
      await login('google');
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async () => {
    setError('');
    setInfo('');
    if (!email) {
      setError('Enter your email address above, then click "Forgot password?"');
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(email);
      setInfo(`If an account exists for ${email}, a password-reset email has been sent. Check your inbox (and spam); it may take a few minutes.`);
    } catch (err) {
      setError(err.message || 'Could not send the reset email');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await login('email', { email, password });
    } catch (err) {
      const msg = err.code === 'auth/invalid-credential'
        ? 'Invalid email or password'
        : err.message || 'Login failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: '#f5f5f5',
    }}>
      <Card sx={{ maxWidth: 420, width: '100%', mx: 2, borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Logo & Title */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: '#1a237e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}>
              <EnergySavingsLeafIcon sx={{ color: '#4caf50', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e' }}>
              Sign in
            </Typography>
            <Typography variant="body2" sx={{ color: '#757575', mt: 0.5 }}>
              to continue to Carbon Measures
            </Typography>
          </Box>

          {(error || authError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {typeof (error || authError) === 'string' ? (error || authError) : (error || authError)?.message || 'An error occurred'}
            </Alert>
          )}

          {info && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>
              {info}
            </Alert>
          )}

          {/* Google Sign-In (hidden unless REACT_APP_ENABLE_GOOGLE_SSO=true) */}
          {GOOGLE_SSO_ENABLED && (
            <>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={handleGoogle}
                disabled={submitting}
                sx={{
                  mb: 2,
                  borderColor: '#4285f4',
                  color: '#4285f4',
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': { borderColor: '#3367d6', bgcolor: 'rgba(66,133,244,0.04)' },
                }}
              >
                Sign in with Google
              </Button>

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" sx={{ color: '#9e9e9e' }}>or</Typography>
              </Divider>
            </>
          )}

          {/* Email/Password Form */}
          <Box component="form" onSubmit={handleEmail}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              variant="outlined"
              disabled={submitting}
              required
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              variant="outlined"
              disabled={submitting}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />

            {/* Remember me / Forgot password */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    size="small"
                    sx={{ color: '#1a237e' }}
                  />
                }
                label={<Typography variant="body2">Remember me</Typography>}
              />
              <Typography
                component="button"
                type="button"
                onClick={handleForgot}
                disabled={submitting}
                variant="body2"
                sx={{
                  color: '#1565c0',
                  background: 'none',
                  border: 'none',
                  p: 0,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Forgot password?
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={submitting}
              sx={{
                bgcolor: '#1565c0',
                textTransform: 'none',
                fontWeight: 600,
                py: 1.2,
                borderRadius: 2,
                fontSize: '1rem',
                '&:hover': { bgcolor: '#0d47a1' },
              }}
            >
              {submitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In'}
            </Button>
          </Box>

          {/* Create account link */}
          <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: '#757575' }}>
            Don&apos;t have an account?{' '}
            <Typography
              component={Link}
              to="/login"
              variant="body2"
              sx={{ color: '#1565c0', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
            >
              Create account
            </Typography>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
